import type { CountryModel } from '@sosocio/creator-types/database';
import type { AddressModel } from '@sosocio/creator-types/project';

export type ResolvedAddressCountry = Pick<CountryModel, 'name' | 'states'>;

/**
 * Assembles the postal lines rendered for a project address without accessing
 * an application country catalog or translation service.
 *
 * @param addressModel Project {@link AddressModel} containing the authored postal fields.
 * @param country Resolved {@link ResolvedAddressCountry} metadata supplied by the host application.
 * @returns Ordered address lines preserving Creator's state-aware postal layout.
 */
export function formatAddress(
	addressModel: AddressModel,
	country?: ResolvedAddressCountry,
): string[] {
	const addressLines = [
		addressModel.name || '',
		addressModel.address1 || '',
	];

	if (addressModel.address2) {
		addressLines.push(addressModel.address2);
	}

	if (!addressModel.country) {
		return addressLines;
	}

	const city = addressModel.city || '';
	const zipcode = addressModel.zipcode || '';

	if (
		country?.states
		&& addressModel.state
	) {
		addressLines.push(`${city} ${addressModel.state} ${zipcode}`);
	} else {
		addressLines.push(`${zipcode}  ${city}`);
	}

	if (addressModel.countryFormatted) {
		addressLines.push(addressModel.countryFormatted);
	} else if (country) {
		addressLines.push(country.name);
	}

	return addressLines;
}
